/**
 * My Tasks data: both task lists (your own reminders, and your team's
 * assigned reminders), their unseen counts, and the fetch/mark-seen/
 * toggle-complete actions. Which list is currently being viewed — a tab, or
 * any other UI selection — is the caller's concern, not this hook's; the
 * actions that need to know simply take a list key when called.
 */
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import {
  ASSIGNED_TASKS,
  fetchAssignedTasksBadgeCount,
  fetchMyTasksList,
  markMyTasksSeen,
  MY_TASKS,
  MyTasksListKey,
  MyTasksRootState,
  selectMyTasksList,
  selectNewlyPostedCount,
  selectRecentlyCompletedCount,
  toggleMyTaskComplete,
} from "../redux/myTasks";

type MyTasksDispatch = ThunkDispatch<MyTasksRootState, undefined, AnyAction>;

export default function useMyTasks() {
  const dispatch = useDispatch<MyTasksDispatch>();

  const myTasks = useSelector((state: MyTasksRootState) => selectMyTasksList(MY_TASKS)(state));
  const assignedTasks = useSelector((state: MyTasksRootState) => selectMyTasksList(ASSIGNED_TASKS)(state));
  const myTasksNewCount = useSelector(selectNewlyPostedCount(MY_TASKS));
  // Assigned Tasks' badge reflects team completions the assigner hasn't
  // seen yet, not newly-created reminders (which the assigner already
  // knows about - they're the one who created them).
  const assignedTasksNewCount = useSelector(selectRecentlyCompletedCount(ASSIGNED_TASKS));

  const fetchList = useCallback(
    (list: MyTasksListKey) => dispatch(fetchMyTasksList(list)),
    [dispatch],
  );

  const fetchAssignedTasksCount = useCallback(
    () => dispatch(fetchAssignedTasksBadgeCount()),
    [dispatch],
  );

  const markSeen = useCallback(
    (list: MyTasksListKey) => dispatch(markMyTasksSeen(list)),
    [dispatch],
  );

  const toggleComplete = useCallback(
    (list: MyTasksListKey, noteId: string, complete: boolean) =>
      dispatch(toggleMyTaskComplete({ list, noteId, complete })),
    [dispatch],
  );

  return {
    myTasks,
    assignedTasks,
    myTasksNewCount,
    assignedTasksNewCount,
    fetchList,
    fetchAssignedTasksCount,
    markSeen,
    toggleComplete,
  };
}
